<template>
  <div class="driver-detail-page">
    <!-- Barra superior con título y acciones -->
    <v-card class="mb-4 elevation-2">
      <v-app-bar flat dense color="primary" dark>
        <v-btn icon @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>Detalles del Conductor</v-toolbar-title>
        <v-spacer></v-spacer>

        <v-btn icon @click="showDriverForm(driver)" v-if="!loading && canEditDriver">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </v-app-bar>
    </v-card>

    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="50" width="5"></v-progress-circular>
      <div class="mt-4 text-subtitle-1 font-weight-medium grey--text text--darken-1">
        Cargando detalles del conductor...
      </div>
    </div>

    <template v-else>
      <v-container>
        <v-row>
          <!-- Panel principal de información del conductor -->
          <v-col cols="12" md="8" lg="7">
            <v-card class="elevation-1">
              <!-- Cabecera con datos principales -->
              <div class="driver-header pa-4">
                <div class="d-flex align-center">
                  <v-avatar color="primary" size="64" class="mr-4">
                    <v-icon size="36" dark>mdi-account-tie</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-h5 font-weight-bold">{{ driver.name }} {{ driver.lastname }}</div>
                  </div>
                </div>
              </div>

              <v-divider></v-divider>

              <!-- Información personal y de contacto -->
              <v-card-text class="py-4">
                <v-row>
                  <!-- Columna Información personal -->
                  <v-col cols="12" md="6">
                    <h3 class="subtitle-1 font-weight-bold mb-3">
                      <v-icon color="primary" small class="mr-2">mdi-account-details</v-icon>
                      Información personal
                    </h3>

                    <v-list dense class="transparent pa-0">
                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-account</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Nombre completo</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ driver.name }} {{ driver.lastname }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-card-account-details</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Documento</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ getDocumentTypeLabel(driver.document_type) }}: {{ driver.document_number }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item v-if="driver.birthdate" class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-calendar</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Fecha de nacimiento</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ formatDate(driver.birthdate) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item v-if="driver.address" class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-home</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Dirección</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ driver.address }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <!-- Información del dealer (solo para colaboradores) 
                      <v-list-item v-if="driver.dealer && $store.getters.HAS_ROLE(['colaborator'])" class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">{{ driver.dealer.is_colaborador ? 'mdi-home' : 'mdi-store' }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Dealer asignado</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1 d-flex align-center">
                            <v-avatar v-if="driver.dealer.imageUrl" size="20" class="mr-2">
                              <img :src="driver.dealer.imageUrl.url" alt="Logo dealer">
                            </v-avatar>
                            {{ driver.dealer.name }}
                            <v-chip v-if="driver.dealer.is_colaborador" x-small class="ml-2" color="primary">
                              Propio
                            </v-chip>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>-->
                    </v-list>
                  </v-col>

                  <!-- Columna Información de contacto -->
                  <v-col cols="12" md="6">
                    <h3 class="subtitle-1 font-weight-bold mb-3">
                      <v-icon color="primary" small class="mr-2">mdi-card-account-phone</v-icon>
                      Información de contacto
                    </h3>

                    <v-list dense class="transparent pa-0">
                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-phone</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Teléfono</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a v-if="driver.phone" :href="`tel:${driver.phone}`" class="text-decoration-none">
                              {{ driver.phone }}
                            </a>
                            <span v-else>No especificado</span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-email</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Correo electrónico</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            <a v-if="driver.email" :href="`mailto:${driver.email}`" class="text-decoration-none">
                              {{ driver.email }}
                            </a>
                            <span v-else>No especificado</span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item v-if="driver.emergency_contact" class="px-0">
                        <v-list-item-icon class="mr-2">
                          <v-icon color="primary">mdi-phone-alert</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Contacto de emergencia</v-list-item-title>
                          <v-list-item-subtitle class="subtitle-1">
                            {{ driver.emergency_contact }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Panel lateral con licencia y estadísticas -->
          <v-col cols="12" md="4" lg="5">
            
            <!-- Fotos del DNI -->
            <v-card class="mb-4 elevation-1">
              <v-card-title class="pb-1">
                <v-icon left color="primary">mdi-card-account-details</v-icon>
                Fotos del DNI
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <v-row>
                  <!-- Parte delantera -->
                  <v-col cols="12" sm="6">
                    <div class="caption grey--text mb-1">
                      <v-icon small class="mr-1">mdi-camera-front</v-icon>Parte delantera
                    </div>
                    <div v-if="driver.document_front_image && driver.document_front_image.url" class="text-center">
                      <v-img
                        :src="driver.document_front_image.url"
                        max-height="160"
                        contain
                        class="grey lighten-4 rounded"
                      >
                        <template v-slot:placeholder>
                          <v-row align="center" justify="center" class="fill-height">
                            <v-progress-circular indeterminate color="primary"></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>
                    </div>
                    <div v-else class="text-center pa-3 grey--text grey lighten-4 rounded">
                      <v-icon size="36" color="grey lighten-1">mdi-image-off</v-icon>
                      <div class="caption mt-1">Sin imagen</div>
                    </div>
                  </v-col>
                  <!-- Parte trasera -->
                  <v-col cols="12" sm="6">
                    <div class="caption grey--text mb-1">
                      <v-icon small class="mr-1">mdi-camera-rear</v-icon>Parte trasera
                    </div>
                    <div v-if="driver.document_back_image && driver.document_back_image.url" class="text-center">
                      <v-img
                        :src="driver.document_back_image.url"
                        max-height="160"
                        contain
                        class="grey lighten-4 rounded"
                      >
                        <template v-slot:placeholder>
                          <v-row align="center" justify="center" class="fill-height">
                            <v-progress-circular indeterminate color="primary"></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>
                    </div>
                    <div v-else class="text-center pa-3 grey--text grey lighten-4 rounded">
                      <v-icon size="36" color="grey lighten-1">mdi-image-off</v-icon>
                      <div class="caption mt-1">Sin imagen</div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
            <!-- Tarjeta de licencia de conducir -->
            <v-card class="mb-4 elevation-1">
              <v-card-title class="pb-1">
                <v-icon left color="primary">mdi-card-account-details-outline</v-icon>
                Licencia de conducir
              </v-card-title>
              <v-divider></v-divider>

                <v-card-text>
                <v-list dense class="transparent pa-0">
                  <div v-if="driver.document_image && driver.document_image.url" class="text-center">
                  <v-img
                    :src="driver.document_image.url"
                    max-height="200"
                    contain
                    class="grey lighten-4 rounded"
                  >
                    <template v-slot:placeholder>
                    <v-row align="center" justify="center" class="fill-height">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </v-row>
                    </template>
                  </v-img>
                  </div>
                  <div v-else class="text-center pa-4 grey--text">
                  <v-icon size="48" color="grey lighten-1">mdi-image-off</v-icon>
                  <div class="mt-2">No hay imagen disponible</div>
                  </div>

                    <v-list-item class="px-0">
                    <v-list-item-icon class="mr-2">
                      <v-icon color="primary">mdi-calendar-clock</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="caption grey--text">Fecha de expiración</v-list-item-title>
                      <v-list-item-subtitle class="subtitle-1">
                        <template v-if="driver.document_expiration_date">
                          <span :class="{ 'red--text': isLicenseExpired(driver.document_expiration_date) }">
                            {{ formatDate(driver.document_expiration_date) }}
                            <v-icon v-if="isLicenseExpired(driver.document_expiration_date)" small color="red">
                              mdi-alert-circle
                            </v-icon>
                          </span>
                        </template>
                        <span v-else>No especificado</span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <!-- Formulario de edición -->
    <driver-form v-if="visibleDriverForm" v-model="editItem" @save="updateDataTable"
      @cancel="visibleDriverForm = false"></driver-form>
  </div>
</template>

<script>
import DriverForm from './DriverForm.vue'
import DriverService from '@/services/DriverService'
import moment from 'moment'
import { mapGetters } from 'vuex'

export default {
  components: {
    DriverForm
  },
  data () {
    return {
      loading: true,
      driver: null,
      visibleDriverForm: false,
      editItem: null,

      // Valores de ejemplo para estadísticas - deberían cargarse desde la API
      movimientosCount: 15,
      ultimoServicio: '24/03/2025'
    }
  },
  computed: {
    ...mapGetters(['HAS_PERMISSION']),
    canEditDriver () {
      return this.HAS_PERMISSION('editar_conductor')
    }
  },
  async created () {
    await this.loadDriverDetails()
  },
  methods: {
    async loadDriverDetails () {
      try {
        this.loading = true
        const driverId = this.$route.params.driver_id
        const { data } = await DriverService.view({ id: driverId, expand: 'dealer,documentFrontImage,documentBackImage' })
        this.driver = data.data

        // Aquí podrías cargar las estadísticas del conductor
        // await this.loadDriverStats(driverId)
      } catch (error) {
        console.error('Error al cargar los detalles del conductor:', error)
        this.$toast.error('Error al cargar los detalles del conductor')
      } finally {
        this.loading = false
      }
    },

    showDriverForm (item) {
      this.editItem = item
      this.visibleDriverForm = true
    },

    updateDataTable () {
      this.visibleDriverForm = false
      this.loadDriverDetails()
      this.$toast.success('Conductor actualizado con éxito')
    },

    formatDate (dateString) {
      if (!dateString) return 'No especificado'
      return moment(dateString).format('DD/MM/YYYY')
    },

    isLicenseExpired (expiryDate) {
      if (!expiryDate) return false
      return moment(expiryDate).isBefore(moment())
    },

    getDocumentTypeLabel (type) {
      const types = {
        'dni': 'DNI',
        'passport': 'Pasaporte',
        'nie': 'NIE',
        'residency': 'Tarjeta de Residencia'
      }
      return types[type] || type
    }
  }
}
</script>

<style scoped>
.driver-header {
  background-color: rgba(0, 0, 0, 0.01);
}

.v-list-item {
  min-height: 0;
  padding-top: 4px;
  padding-bottom: 4px;
}

/* Transiciones y efectos */
.v-card {
  transition: box-shadow 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}
</style>
